import {
  Document, Paragraph, TextRun, Packer, Table, TableRow, TableCell,
  WidthType, BorderStyle, AlignmentType, ShadingType,
  PageBreak, TabStopType, Header, Footer, PageNumber, LevelFormat,
} from 'docx';
import { saveAs } from 'file-saver';

// ── Color constants ───────────────────────────────────────────────────────────
const NAVY       = '0F3460';
const ACCENT     = '2D7DD2';
const LIGHT_GRAY = 'F3F4F6';
const ALT_ROW    = 'F9FAFB';
const WHITE      = 'FFFFFF';
const DARK_TEXT  = '1A1A2E';
const MUTED      = '6B7280';
const BORDER_CLR = 'D1D5DB';

const CONTENT_WIDTH = 9360;
const CELL_MGN      = { top: 80, bottom: 80, left: 120, right: 120 };

function thinBorder() {
  return {
    top:    { style: BorderStyle.SINGLE, size: 4, color: BORDER_CLR },
    bottom: { style: BorderStyle.SINGLE, size: 4, color: BORDER_CLR },
    left:   { style: BorderStyle.SINGLE, size: 4, color: BORDER_CLR },
    right:  { style: BorderStyle.SINGLE, size: 4, color: BORDER_CLR },
  };
}

function noBorder() {
  return {
    top:     { style: BorderStyle.NONE, size: 0, color: 'auto' },
    bottom:  { style: BorderStyle.NONE, size: 0, color: 'auto' },
    left:    { style: BorderStyle.NONE, size: 0, color: 'auto' },
    right:   { style: BorderStyle.NONE, size: 0, color: 'auto' },
    insideH: { style: BorderStyle.NONE, size: 0, color: 'auto' },
    insideV: { style: BorderStyle.NONE, size: 0, color: 'auto' },
  };
}

// ── Numbering (bullets / decimal lists) ──────────────────────────────────────
const NUMBERING_CONFIG = [
  {
    reference: 'bullet-list',
    levels: [{
      level: 0,
      format: LevelFormat.BULLET,
      text: '\u2022',
      alignment: AlignmentType.LEFT,
      style: {
        paragraph: { indent: { left: 480, hanging: 240 } },
        run: { font: 'Symbol', size: 22 },
      },
    }],
  },
  {
    reference: 'decimal-list',
    levels: [{
      level: 0,
      format: LevelFormat.DECIMAL,
      text: '%1.',
      alignment: AlignmentType.LEFT,
      style: {
        paragraph: { indent: { left: 480, hanging: 240 } },
        run: { font: 'Calibri', size: 22 },
      },
    }],
  },
];

// ── Document-level styles ─────────────────────────────────────────────────────
const DOC_STYLES = {
  default: {
    document: { run: { font: 'Calibri', size: 22 } },
  },
  paragraphStyles: [
    {
      id: 'Heading1',
      name: 'Heading 1',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: { size: 28, bold: true, font: 'Calibri', color: NAVY },
      paragraph: {
        spacing: { before: 360, after: 180 },
        outlineLevel: 0,
        border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: ACCENT, space: 4 } },
      },
    },
    {
      id: 'Heading2',
      name: 'Heading 2',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: { size: 24, bold: true, font: 'Calibri', color: NAVY },
      paragraph: { spacing: { before: 240, after: 120 }, outlineLevel: 1 },
    },
    {
      id: 'Heading3',
      name: 'Heading 3',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: { size: 22, bold: true, font: 'Calibri', color: ACCENT },
      paragraph: { spacing: { before: 180, after: 80 }, outlineLevel: 2 },
    },
  ],
};

// ── Paragraph helpers ─────────────────────────────────────────────────────────
function h1(text) {
  return new Paragraph({ style: 'Heading1', children: [new TextRun({ text, font: 'Calibri' })] });
}

function h2(text) {
  return new Paragraph({ style: 'Heading2', children: [new TextRun({ text, font: 'Calibri' })] });
}

function body(text, bold = false) {
  return new Paragraph({
    children: [new TextRun({ text, font: 'Calibri', size: 22, bold })],
    spacing: { after: 120 },
  });
}

function blank() {
  return new Paragraph({ children: [new TextRun({ text: '' })], spacing: { after: 80 } });
}

function bullet(text) {
  return new Paragraph({
    numbering: { reference: 'bullet-list', level: 0 },
    children: [new TextRun({ text, font: 'Calibri', size: 22 })],
    spacing: { after: 80 },
  });
}

function decimal(text) {
  return new Paragraph({
    numbering: { reference: 'decimal-list', level: 0 },
    children: [new TextRun({ text, font: 'Calibri', size: 22 })],
    spacing: { after: 80 },
  });
}

// ── Table helper ──────────────────────────────────────────────────────────────
function styledTable(colWidths, headerLabels, dataRows) {
  const hdrRow = new TableRow({
    tableHeader: true,
    children: headerLabels.map((text, i) =>
      new TableCell({
        width: { size: colWidths[i], type: WidthType.DXA },
        margins: CELL_MGN,
        shading: { type: ShadingType.CLEAR, fill: NAVY },
        borders: thinBorder(),
        children: [new Paragraph({
          children: [new TextRun({ text, bold: true, size: 20, color: WHITE, font: 'Calibri' })],
          spacing: { after: 0 },
        })],
      })
    ),
  });

  const bodyRows = dataRows.map((cells, ri) =>
    new TableRow({
      children: cells.map((text, i) =>
        new TableCell({
          width: { size: colWidths[i], type: WidthType.DXA },
          margins: CELL_MGN,
          shading: { type: ShadingType.CLEAR, fill: ri % 2 === 0 ? WHITE : ALT_ROW },
          borders: thinBorder(),
          children: [new Paragraph({
            children: [new TextRun({ text: String(text || ''), size: 20, color: DARK_TEXT, font: 'Calibri' })],
            spacing: { after: 0 },
          })],
        })
      ),
    })
  );

  return new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    columnWidths: colWidths,
    rows: [hdrRow, ...bodyRows],
  });
}

// ── Letterhead block ──────────────────────────────────────────────────────────
function makeLetterhead(companyName, cageCode, dateStr) {
  return new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    columnWidths: [6480, 2880],
    borders: noBorder(),
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: 6480, type: WidthType.DXA },
            borders: noBorder(),
            margins: CELL_MGN,
            children: [
              new Paragraph({ children: [new TextRun({ text: 'TheClearAudit', bold: true, size: 44, color: NAVY, font: 'Calibri' })], spacing: { after: 60 } }),
              new Paragraph({ children: [new TextRun({ text: 'theclearaudit.com', size: 22, color: MUTED, font: 'Calibri' })], spacing: { after: 40 } }),
              new Paragraph({ children: [new TextRun({ text: 'theclearaudit@gmail.com', size: 22, color: MUTED, font: 'Calibri' })], spacing: { after: 0 } }),
            ],
          }),
          new TableCell({
            width: { size: 2880, type: WidthType.DXA },
            borders: noBorder(),
            margins: CELL_MGN,
            children: [
              new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: companyName, bold: true, size: 36, color: NAVY, font: 'Calibri' })], spacing: { after: 60 } }),
              new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: cageCode ? `CAGE Code: ${cageCode}` : '', size: 20, color: MUTED, font: 'Calibri' })], spacing: { after: 40 } }),
              new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: dateStr, size: 20, color: MUTED, font: 'Calibri' })], spacing: { after: 0 } }),
            ],
          }),
        ],
      }),
    ],
  });
}

// ── Document information table ────────────────────────────────────────────────
function makeDocInfoTable(companyName, cageCode, dateStr, reviewDate, approvedBy, reviewedBy) {
  const rows = [
    ['Organization Name',     companyName],
    ['CAGE Code',             cageCode || 'Pending'],
    ['Policy Version',        '1.0'],
    ['Effective Date',        dateStr],
    ['Review Date',           reviewDate],
    ['Approved By',           approvedBy],
    ['Reviewed By',           reviewedBy],
    ['Classification',        'CUI — Controlled Unclassified Information'],
    ['Applicable Framework',  'CMMC 2.0 Level 2 / NIST SP 800-171'],
  ];

  return new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    columnWidths: [2880, 6480],
    rows: rows.map(([label, value]) =>
      new TableRow({
        children: [
          new TableCell({
            width: { size: 2880, type: WidthType.DXA },
            borders: thinBorder(),
            margins: CELL_MGN,
            shading: { type: ShadingType.CLEAR, fill: LIGHT_GRAY },
            children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, size: 20, color: NAVY, font: 'Calibri' })], spacing: { after: 0 } })],
          }),
          new TableCell({
            width: { size: 6480, type: WidthType.DXA },
            borders: thinBorder(),
            margins: CELL_MGN,
            shading: { type: ShadingType.CLEAR, fill: WHITE },
            children: [new Paragraph({ children: [new TextRun({ text: value || '', size: 20, color: DARK_TEXT, font: 'Calibri' })], spacing: { after: 0 } })],
          }),
        ],
      })
    ),
  });
}

// ── Core document builder ─────────────────────────────────────────────────────
async function buildDoc({ title, subtitle, company, content, filename }) {
  const today      = new Date();
  const dateStr    = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const reviewDate = new Date(today.getTime() + 365 * 24 * 60 * 60 * 1000)
    .toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const companyName = company?.company_name || 'Your Organization';
  const cageCode    = company?.cage_code    || '';
  const approvedBy  = company?.approved_by  || 'Authorized Representative';
  const reviewedBy  = company?.reviewed_by  || 'Pending Review';
  const fileSlug    = companyName.replace(/\s+/g, '_');

  const doc = new Document({
    numbering: { config: NUMBERING_CONFIG },
    styles: DOC_STYLES,
    sections: [{
      properties: {
        page: {
          size:   { width: 12240, height: 15840 },
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
        },
      },
      headers: {
        default: new Header({
          children: [
            new Paragraph({
              children: [
                new TextRun({ text: `${companyName} — `, font: 'Calibri', size: 16, color: MUTED }),
                new TextRun({ text: title, font: 'Calibri', size: 16, color: MUTED }),
                new TextRun({ text: '\t', font: 'Calibri' }),
                new TextRun({ text: 'CONFIDENTIAL — CUI', font: 'Calibri', size: 16, bold: true, color: NAVY }),
              ],
              tabStops: [{ type: TabStopType.RIGHT, position: CONTENT_WIDTH }],
              border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: 'E5E7EB', space: 4 } },
            }),
          ],
        }),
      },
      footers: {
        default: new Footer({
          children: [
            new Paragraph({
              children: [
                new TextRun({ text: 'Prepared with TheClearAudit | theclearaudit.com', font: 'Calibri', size: 14, color: '9CA3AF' }),
                new TextRun({ text: '\t', font: 'Calibri' }),
                new TextRun({ text: 'Page ', font: 'Calibri', size: 14, color: MUTED }),
                new TextRun({ children: [PageNumber.CURRENT], font: 'Calibri', size: 14, color: MUTED }),
              ],
              tabStops: [{ type: TabStopType.RIGHT, position: CONTENT_WIDTH }],
              border: { top: { style: BorderStyle.SINGLE, size: 4, color: 'E5E7EB', space: 4 } },
            }),
          ],
        }),
      },
      children: [
        makeLetterhead(companyName, cageCode, dateStr),
        // Thick divider line
        new Paragraph({
          children: [new TextRun({ text: '' })],
          border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: NAVY, space: 4 } },
          spacing: { before: 160, after: 160 },
        }),
        // Centered document title
        new Paragraph({
          alignment: AlignmentType.CENTER,
          children: [new TextRun({ text: title, bold: true, size: 56, color: NAVY, font: 'Calibri' })],
          spacing: { before: 240, after: 120 },
        }),
        // Subtitle / category
        new Paragraph({
          alignment: AlignmentType.CENTER,
          children: [new TextRun({ text: subtitle, size: 24, color: MUTED, font: 'Calibri' })],
          spacing: { after: 480 },
        }),
        // Document information table
        makeDocInfoTable(companyName, cageCode, dateStr, reviewDate, approvedBy, reviewedBy),
        // Page break — policy content starts on page 2
        new Paragraph({ children: [new PageBreak()], spacing: { after: 0 } }),
        ...content,
      ],
    }],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${fileSlug}_${filename}.docx`);
}

// ── ACCESS CONTROL POLICY ─────────────────────────────────────────────────────
export async function generateAccessControlPolicy(profile) {
  const co = profile?.company_name || 'Your Organization';
  await buildDoc({
    title:    'ACCESS CONTROL POLICY',
    subtitle: 'Access Control — CMMC 2.0 Level 2 / NIST SP 800-171',
    company:  profile,
    filename: 'Access_Control_Policy',
    content: [
      h1('1. Purpose'),
      body(`This Access Control Policy establishes the requirements for managing access to ${co}'s information systems and Controlled Unclassified Information (CUI) in accordance with CMMC Level 2 requirements and NIST SP 800-171 controls AC.L2-3.1.1 through AC.L2-3.1.22.`),

      h1('2. Scope'),
      body(`This policy applies to all employees, contractors, and third-party users who access ${co}'s information systems, networks, and data including any system that stores, processes, or transmits CUI.`),

      h1('3. Policy Statements'),

      h2('3.1 Account Management'),
      body('3.1.1 All user accounts must be formally requested, approved, and documented before access is granted.'),
      body('3.1.2 User accounts must be reviewed at least quarterly and immediately upon role changes or termination.'),
      body('3.1.3 Shared or group accounts are prohibited except where technically required and formally approved.'),
      body('3.1.4 Accounts inactive for 30 days or more must be disabled pending review.'),

      h2('3.2 Least Privilege'),
      body('3.2.1 Users must be granted the minimum access necessary to perform their job functions.'),
      body('3.2.2 Privileged access (administrator rights) must be separate from standard user accounts.'),
      body('3.2.3 Privileged accounts must be reviewed monthly and access revoked when no longer required.'),
      body('3.2.4 Access to CUI must be explicitly authorized and documented.'),

      h2('3.3 Remote Access'),
      body('3.3.1 All remote access to company systems must use encrypted connections (VPN or equivalent).'),
      body('3.3.2 Multi-factor authentication (MFA) is required for all remote access sessions.'),
      body('3.3.3 Remote access sessions must automatically terminate after 15 minutes of inactivity.'),
      body('3.3.4 Remote access from foreign countries requires written approval from the IT Director.'),

      h2('3.4 Access Control for Mobile Devices'),
      body('3.4.1 Mobile devices that access CUI must be enrolled in the Mobile Device Management (MDM) system.'),
      body('3.4.2 Mobile devices must have screen lock enabled with a minimum 6-digit PIN.'),
      body('3.4.3 Lost or stolen devices must be reported immediately and remotely wiped within 24 hours.'),

      h2('3.5 System Access Control'),
      body('3.5.1 Systems must display a warning banner before login stating authorized use only.'),
      body('3.5.2 Sessions must lock after 15 minutes of inactivity and require re-authentication.'),
      body('3.5.3 Three consecutive failed login attempts must result in account lockout.'),

      h1('4. Roles and Responsibilities'),
      styledTable(
        [2880, 6480],
        ['Role', 'Responsibilities'],
        [
          ['IT Administrator',   'Manages user accounts, enforces access controls, and maintains access logs.'],
          ['Managers',           'Approve access requests for direct reports and report personnel changes within 24 hours.'],
          ['All Users',          'Follow this policy, protect credentials, and report suspected unauthorized access.'],
          ['CISO / IT Director', 'Owns this policy, conducts quarterly reviews, and approves exceptions.'],
        ]
      ),
      blank(),

      h1('5. Enforcement'),
      body(`Violations of this policy may result in disciplinary action up to and including termination of employment or contract. Violations that result in unauthorized access to CUI will be reported to the appropriate DoD contracting officer.`),

      h1('6. Related Documents'),
      decimal('NIST SP 800-171 Rev 2 — Access Control Family (3.1.1 - 3.1.22)'),
      decimal('System Security Plan (SSP)'),
      decimal('Incident Response Policy'),
      decimal('Acceptable Use Policy'),

      h1('7. Review and Approval'),
      body('This policy must be reviewed annually or when significant changes occur to the system environment. All changes must be approved by the designated senior official.'),
    ],
  });
}

// ── INCIDENT RESPONSE POLICY ──────────────────────────────────────────────────
export async function generateIncidentResponsePolicy(profile) {
  const co = profile?.company_name || 'Your Organization';
  await buildDoc({
    title:    'INCIDENT RESPONSE POLICY',
    subtitle: 'Incident Response — CMMC 2.0 Level 2 / NIST SP 800-171',
    company:  profile,
    filename: 'Incident_Response_Policy',
    content: [
      h1('1. Purpose'),
      body(`This Incident Response Policy establishes ${co}'s approach to detecting, reporting, and responding to cybersecurity incidents affecting Controlled Unclassified Information (CUI) in accordance with CMMC Level 2 requirements and NIST SP 800-171 controls IR.L2-3.6.1 through IR.L2-3.6.3.`),

      h1('2. Scope'),
      body(`This policy applies to all information systems, personnel, and third parties that store, process, or transmit CUI on behalf of ${co}.`),

      h1('3. Incident Classification'),
      styledTable(
        [1440, 1800, 3240, 2880],
        ['Category', 'Severity', 'Description', 'Response Time'],
        [
          ['1', 'Critical', 'Confirmed breach of CUI, ransomware, or system compromise', 'Within 1 hour'],
          ['2', 'High',     'Suspected breach, malware infection, or unauthorized access attempt', 'Within 4 hours'],
          ['3', 'Medium',   'Policy violation, phishing attempt, or suspicious activity', 'Within 24 hours'],
          ['4', 'Low',      'Minor security event with no CUI impact', 'Within 72 hours'],
        ]
      ),
      blank(),

      h1('4. Incident Response Procedures'),

      h2('4.1 Detection and Reporting'),
      body('4.1.1 All employees must report suspected security incidents immediately to the IT Help Desk or Security Team.'),
      body('4.1.2 Reports must include: date/time discovered, systems affected, description of the incident, and reporter contact information.'),
      body('4.1.3 Employees must NOT attempt to investigate or remediate incidents independently.'),

      h2('4.2 Containment'),
      body('4.2.1 The IT team must immediately isolate affected systems from the network.'),
      body('4.2.2 Credentials that may be compromised must be reset immediately.'),
      body('4.2.3 Evidence must be preserved — do not power off systems without IT authorization.'),

      h2('4.3 DoD Reporting Requirements'),
      body('4.3.1 Cyber incidents affecting CUI must be reported to the DoD via the DIBNet portal within 72 hours of discovery.'),
      body('4.3.2 The report must include a description of the incident, systems affected, and impact assessment.'),
      body('4.3.3 The assigned contracting officer must be notified within 24 hours of any confirmed CUI breach.'),
      body('4.3.4 DFARS 252.204-7012 reporting requirements must be followed for all covered cyber incidents.'),

      h2('4.4 Recovery'),
      body('4.4.1 Systems must be fully remediated and verified before returning to production.'),
      body('4.4.2 A post-incident review must be conducted within 5 business days.'),
      body('4.4.3 Lessons learned must be documented and used to update security controls.'),

      h1('5. Incident Response Team'),
      styledTable(
        [2880, 2880, 3600],
        ['Role', 'Contact', 'Responsibility'],
        [
          ['Incident Response Lead',  '[Name / Title]', 'Overall coordination and DoD reporting'],
          ['IT Administrator',        '[Name]',         'Technical containment and recovery'],
          ['Legal / Compliance',      '[Name / Title]', 'Legal obligations and notification'],
          ['Senior Management',       '[Name / Title]', 'Executive decisions and resource allocation'],
        ]
      ),
      blank(),

      h1('6. Contact Information'),
      styledTable(
        [3600, 5760],
        ['Contact', 'Details'],
        [
          ['Internal Security Team',   '[Phone Number and Email]'],
          ['DoD Cyber Crime Center',   'dibnet.dod.mil'],
          ['US-CERT',                  '1-888-282-0870 | www.us-cert.gov'],
          ['FBI Cyber Division',       '1-855-292-3937'],
        ]
      ),
      blank(),

      h1('7. Related Documents'),
      decimal('NIST SP 800-171 Rev 2 — Incident Response (3.6.1 - 3.6.3)'),
      decimal('DFARS Clause 252.204-7012'),
      decimal('System Security Plan (SSP)'),
      decimal('Business Continuity Plan'),

      h1('8. Review and Approval'),
      body('This policy must be reviewed annually and after every major incident. Updates must be approved by the Incident Response Lead and senior management.'),
    ],
  });
}

// ── CONFIGURATION MANAGEMENT POLICY ──────────────────────────────────────────
export async function generateConfigManagementPolicy(profile) {
  const co = profile?.company_name || 'Your Organization';
  await buildDoc({
    title:    'CONFIGURATION MANAGEMENT POLICY',
    subtitle: 'Configuration Management — CMMC 2.0 Level 2 / NIST SP 800-171',
    company:  profile,
    filename: 'Configuration_Management_Policy',
    content: [
      h1('1. Purpose'),
      body(`This Configuration Management Policy establishes ${co}'s requirements for managing the configuration of information systems that store, process, or transmit CUI in accordance with CMMC Level 2 and NIST SP 800-171 controls CM.L2-3.4.1 through CM.L2-3.4.9.`),

      h1('2. Scope'),
      body(`This policy applies to all hardware, software, and network components within ${co}'s CUI boundary including servers, workstations, network devices, mobile devices, and cloud services.`),

      h1('3. Baseline Configuration'),
      body('3.1 A baseline configuration must be established and documented for all system components.'),
      body('3.2 Baselines must reflect the principle of least functionality — only required services enabled.'),
      body('3.3 Baselines must be reviewed and updated at least annually or after significant changes.'),
      body('3.4 Deviations from the baseline must be documented and approved before implementation.'),

      h1('4. Change Control'),
      body('4.1 All changes to production systems must go through the formal change control process.'),
      body('4.2 Changes must be tested in a non-production environment before deployment.'),
      body('4.3 Emergency changes must be documented and reviewed within 24 hours of implementation.'),
      body('4.4 Unauthorized changes must be reported as security incidents.'),

      h1('5. Software Management'),
      body('5.1 Only approved software may be installed on company systems. An approved software list must be maintained.'),
      body('5.2 Software installation by end users is prohibited without IT approval.'),
      body('5.3 Unlicensed or pirated software is strictly prohibited.'),
      body('5.4 Software must be kept current with security patches within 30 days of release for critical patches.'),

      h1('6. Vulnerability Management'),
      body('6.1 Vulnerability scans must be conducted at least monthly on all in-scope systems.'),
      body('6.2 Critical vulnerabilities must be remediated within 30 days of discovery.'),
      body('6.3 High vulnerabilities must be remediated within 60 days.'),
      body('6.4 All vulnerability scan results must be documented and tracked to closure.'),

      h1('7. Roles and Responsibilities'),
      styledTable(
        [2880, 6480],
        ['Role', 'Responsibilities'],
        [
          ['IT Administrator',     'Maintains baseline configurations, enforces change control, manages software inventory.'],
          ['Change Advisory Board', 'Reviews and approves all production changes. Meets as needed.'],
          ['System Owners',         'Request configuration changes and ensure changes are properly documented.'],
          ['CISO / IT Director',    'Owns this policy, approves emergency changes, oversees vulnerability remediation.'],
        ]
      ),
      blank(),

      h1('8. Related Documents'),
      decimal('NIST SP 800-171 Rev 2 — Configuration Management (3.4.1 - 3.4.9)'),
      decimal('System Security Plan (SSP)'),
      decimal('Change Management Procedures'),
      decimal('Software Inventory'),

      h1('9. Review and Approval'),
      body('This policy must be reviewed annually or when significant changes occur to the system architecture. All changes must be approved by the CISO or IT Director.'),
    ],
  });
}

// ── MEDIA PROTECTION POLICY ───────────────────────────────────────────────────
export async function generateMediaProtectionPolicy(profile) {
  const co = profile?.company_name || 'Your Organization';
  await buildDoc({
    title:    'MEDIA PROTECTION POLICY',
    subtitle: 'Media Protection — CMMC 2.0 Level 2 / NIST SP 800-171',
    company:  profile,
    filename: 'Media_Protection_Policy',
    content: [
      h1('1. Purpose'),
      body(`This Media Protection Policy establishes ${co}'s requirements for protecting digital and physical media containing CUI in accordance with CMMC Level 2 and NIST SP 800-171 controls MP.L2-3.8.1 through MP.L2-3.8.9.`),

      h1('2. Scope'),
      body('This policy applies to all digital media (USB drives, hard drives, CDs, tapes, mobile devices) and physical media (paper documents, printouts) containing CUI.'),

      h1('3. Media Access Controls'),
      body('3.1 Access to media containing CUI must be restricted to authorized personnel only.'),
      body('3.2 Media containing CUI must be labeled with appropriate CUI markings.'),
      body('3.3 A media inventory must be maintained and reviewed quarterly.'),

      h1('4. Media Transport'),
      body('4.1 CUI media being transported outside the facility must be encrypted using FIPS 140-2 validated encryption.'),
      body('4.2 Physical media transport must use tamper-evident packaging and documented chain of custody.'),
      body('4.3 CUI media must never be checked as airline baggage — carry-on only.'),
      body('4.4 Loss or theft of CUI media must be reported immediately as a security incident.'),

      h1('5. Media Sanitization and Disposal'),
      styledTable(
        [2160, 3600, 3600],
        ['Media Type', 'Sanitization Method', 'Documentation Required'],
        [
          ['Hard Drives',       'DoD 5220.22-M wipe or physical destruction', 'Destruction certificate required'],
          ['USB / Flash Drives', 'FIPS-compliant wipe or physical destruction', 'Log with date, method, personnel'],
          ['Paper Documents',   'Cross-cut shred to DIN 66399 Level P-4+',   'Shred log or third-party certificate'],
          ['Mobile Devices',    'Factory reset + MDM remote wipe',             'MDM wipe confirmation log'],
        ]
      ),
      blank(),
      body('5.5 A third-party certificate of destruction is required for outsourced disposal.'),

      h1('6. Removable Media'),
      body('6.1 Use of removable media (USB drives) is restricted. All removable media must be approved by IT.'),
      body('6.2 Personally owned removable media is prohibited on company systems.'),
      body('6.3 All approved removable media must be encrypted and logged.'),

      h1('7. Roles and Responsibilities'),
      styledTable(
        [2880, 6480],
        ['Role', 'Responsibilities'],
        [
          ['IT Administrator',   'Maintains media inventory, approves removable media, oversees sanitization.'],
          ['All Employees',      'Label CUI media correctly, report lost/stolen media immediately.'],
          ['Facility Manager',   'Ensures physical security of media storage areas and shredding bins.'],
          ['CISO / IT Director', 'Owns this policy, approves media transport exceptions, reviews inventory.'],
        ]
      ),
      blank(),

      h1('8. Related Documents'),
      decimal('NIST SP 800-171 Rev 2 — Media Protection (3.8.1 - 3.8.9)'),
      decimal('NIST SP 800-88 — Guidelines for Media Sanitization'),
      decimal('System Security Plan (SSP)'),
      decimal('Data Classification Policy'),

      h1('9. Review and Approval'),
      body('This policy must be reviewed annually. The CISO or IT Director must approve all updates.'),
    ],
  });
}

// ── SYSTEM AND COMMUNICATIONS PROTECTION POLICY ───────────────────────────────
export async function generateSystemCommPolicy(profile) {
  const co = profile?.company_name || 'Your Organization';
  await buildDoc({
    title:    'SYSTEM AND COMMUNICATIONS PROTECTION POLICY',
    subtitle: 'System and Communications Protection — CMMC 2.0 Level 2 / NIST SP 800-171',
    company:  profile,
    filename: 'System_Communications_Protection_Policy',
    content: [
      h1('1. Purpose'),
      body(`This policy establishes ${co}'s requirements for protecting information systems and communications in accordance with CMMC Level 2 and NIST SP 800-171 controls SC.L2-3.13.1 through SC.L2-3.13.16.`),

      h1('2. Scope'),
      body(`This policy applies to all information systems, network infrastructure, and communication channels used by ${co} that store, process, or transmit CUI.`),

      h1('3. Network Segmentation'),
      body('3.1 CUI systems must be logically or physically separated from general corporate networks.'),
      body('3.2 Network segmentation must be enforced through firewalls, VLANs, or equivalent controls.'),
      body('3.3 Network diagrams must be maintained and reviewed annually.'),
      body('3.4 Connections between CUI and non-CUI networks must be minimized and documented.'),

      h1('4. Encryption Requirements'),
      styledTable(
        [2160, 3600, 3600],
        ['Data State', 'Requirement', 'Standard'],
        [
          ['At Rest',             'Full disk / volume encryption required for all CUI storage', 'AES-256 / FIPS 140-2'],
          ['In Transit (Public)', 'Encrypted channel required for all public network transmissions', 'TLS 1.2 or higher'],
          ['Email',               'Encrypted email required when transmitting CUI externally', 'S/MIME or equivalent'],
          ['Wireless',            'Enterprise wireless security required for CUI networks', 'WPA3 or WPA2-Enterprise'],
        ]
      ),
      blank(),

      h1('5. Boundary Protection'),
      body('5.1 A firewall must be deployed at all network boundaries to control inbound and outbound traffic.'),
      body('5.2 Firewall rules must be reviewed quarterly and documented.'),
      body('5.3 Unnecessary ports, protocols, and services must be disabled.'),
      body('5.4 Intrusion detection or prevention systems must be deployed on CUI networks.'),

      h1('6. Remote Access Security'),
      body('6.1 All remote access must use encrypted VPN connections.'),
      body('6.2 Split tunneling is prohibited for remote access sessions involving CUI.'),
      body('6.3 Remote access must require multi-factor authentication.'),

      h1('7. Roles and Responsibilities'),
      styledTable(
        [2880, 6480],
        ['Role', 'Responsibilities'],
        [
          ['Network Administrator', 'Configures and maintains firewalls, VLANs, and network segmentation controls.'],
          ['IT Administrator',      'Enforces encryption requirements, manages VPN and remote access systems.'],
          ['Security Team',         'Reviews firewall rules, monitors intrusion detection alerts, conducts network audits.'],
          ['CISO / IT Director',    'Owns this policy, approves architecture changes, reviews annual network diagrams.'],
        ]
      ),
      blank(),

      h1('8. Related Documents'),
      decimal('NIST SP 800-171 Rev 2 — System and Communications Protection (3.13.1 - 3.13.16)'),
      decimal('System Security Plan (SSP)'),
      decimal('Network Architecture Diagram'),
      decimal('Access Control Policy'),

      h1('9. Review and Approval'),
      body('This policy must be reviewed annually or upon significant changes to network architecture. All updates require approval from the CISO or IT Director.'),
    ],
  });
}

// ── SECURITY AWARENESS AND TRAINING POLICY ────────────────────────────────────
export async function generateTrainingPolicy(profile) {
  const co = profile?.company_name || 'Your Organization';
  await buildDoc({
    title:    'SECURITY AWARENESS AND TRAINING POLICY',
    subtitle: 'Awareness and Training — CMMC 2.0 Level 2 / NIST SP 800-171',
    company:  profile,
    filename: 'Security_Awareness_Training_Policy',
    content: [
      h1('1. Purpose'),
      body(`This policy establishes ${co}'s requirements for security awareness and training in accordance with CMMC Level 2 and NIST SP 800-171 controls AT.L2-3.2.1 through AT.L2-3.2.3.`),

      h1('2. Scope'),
      body(`This policy applies to all employees, contractors, and third-party users who access ${co}'s systems or handle CUI.`),

      h1('3. Training Requirements'),
      body('3.1 All employees and contractors with access to CUI must complete security awareness training before accessing any company systems.'),
      body('3.2 Annual refresher training is required for all personnel.'),
      body('3.3 Role-based training must be provided to personnel with elevated privileges or CUI handling responsibilities.'),
      body('3.4 Training must cover: phishing awareness, password security, CUI handling, incident reporting, and acceptable use.'),

      h1('4. Required Training Topics'),
      bullet('Recognizing and reporting phishing and social engineering attacks'),
      bullet('Proper handling, marking, and storage of CUI'),
      bullet('Password creation and management best practices'),
      bullet('Physical security and clean desk policy'),
      bullet('Incident reporting procedures'),
      bullet('Mobile device security'),
      bullet('Working remotely securely'),
      bullet('Consequences of security policy violations'),
      blank(),

      h1('5. Training Records'),
      body('5.1 Training completion records must be maintained for all personnel.'),
      body('5.2 Records must include: employee name, training title, completion date, and pass/fail status.'),
      body('5.3 Training records must be retained for a minimum of 3 years.'),
      body('5.4 Non-compliant personnel must be restricted from CUI access until training is completed.'),

      h1('6. Insider Threat Awareness'),
      body('6.1 Training must include insider threat awareness covering warning signs and reporting procedures.'),
      body('6.2 Employees must understand their obligation to report suspicious behavior.'),
      body('6.3 Retaliation against those who report concerns in good faith is strictly prohibited.'),

      h1('7. Roles and Responsibilities'),
      styledTable(
        [2880, 6480],
        ['Role', 'Responsibilities'],
        [
          ['HR / People Operations', 'Ensures all new hires complete initial security training before system access.'],
          ['Managers',               'Verify direct reports complete annual training by deadlines. Report non-compliance.'],
          ['IT Administrator',       'Manages training platform, generates completion reports, restricts non-compliant access.'],
          ['CISO / IT Director',     'Owns this policy, selects training content, reviews records annually.'],
        ]
      ),
      blank(),

      h1('8. Related Documents'),
      decimal('NIST SP 800-171 Rev 2 — Awareness and Training (3.2.1 - 3.2.3)'),
      decimal('System Security Plan (SSP)'),
      decimal('Acceptable Use Policy'),
      decimal('Incident Response Policy'),

      h1('9. Review and Approval'),
      body('This policy must be reviewed annually. Training content must be updated to reflect current threats and regulatory requirements.'),
    ],
  });
}

// ── RISK ASSESSMENT POLICY ────────────────────────────────────────────────────
export async function generateRiskAssessmentPolicy(profile) {
  const co = profile?.company_name || 'Your Organization';
  await buildDoc({
    title:    'RISK ASSESSMENT POLICY',
    subtitle: 'Risk Assessment — CMMC 2.0 Level 2 / NIST SP 800-171',
    company:  profile,
    filename: 'Risk_Assessment_Policy',
    content: [
      h1('1. Purpose'),
      body(`This Risk Assessment Policy establishes ${co}'s requirements for conducting and documenting risk assessments in accordance with CMMC Level 2 and NIST SP 800-171 controls RA.L2-3.11.1 through RA.L2-3.11.3.`),

      h1('2. Scope'),
      body(`This policy applies to all information systems, data flows, and personnel within ${co}'s CUI boundary.`),

      h1('3. Risk Assessment Frequency'),
      body('3.1 A formal risk assessment must be conducted at least annually.'),
      body('3.2 Risk assessments must also be conducted when significant changes occur including new systems, new CUI flows, or significant threat changes.'),
      body('3.3 Results must be documented and approved by senior management.'),

      h1('4. Risk Assessment Process'),
      styledTable(
        [720, 2160, 6480],
        ['Step', 'Activity', 'Description'],
        [
          ['1', 'System Characterization',  'Identify all systems, data flows, and CUI boundaries in scope.'],
          ['2', 'Threat Identification',    'Identify potential threats to CUI including cyber, physical, and insider threats.'],
          ['3', 'Vulnerability Identification', 'Identify vulnerabilities through scanning, testing, and security control review.'],
          ['4', 'Risk Determination',       'Assess the likelihood and impact of each threat exploiting each vulnerability.'],
          ['5', 'Risk Response',            'Determine appropriate responses — accept, mitigate, transfer, or avoid.'],
          ['6', 'Documentation',            'Document findings in a Risk Register and update the POA&M.'],
        ]
      ),
      blank(),

      h1('5. Risk Register'),
      body('5.1 A Risk Register must be maintained documenting all identified risks.'),
      body('5.2 Each risk entry must include: description, likelihood, impact, risk level, owner, and remediation plan.'),
      body('5.3 The Risk Register must be reviewed quarterly by the security team.'),
      body('5.4 High risks must be escalated to senior management within 5 business days.'),

      h1('6. Vulnerability Scanning'),
      body('6.1 Automated vulnerability scans must be run at least monthly.'),
      body('6.2 Penetration testing must be conducted at least annually by qualified personnel.'),
      body('6.3 Scan results must be documented and used to update the risk assessment.'),

      h1('7. Roles and Responsibilities'),
      styledTable(
        [2880, 6480],
        ['Role', 'Responsibilities'],
        [
          ['Risk Assessment Lead',  'Plans and coordinates annual risk assessments, maintains the Risk Register.'],
          ['IT Administrator',      'Executes vulnerability scans, provides technical input on system configurations.'],
          ['System Owners',         'Participate in risk assessment activities and own remediation of identified risks.'],
          ['CISO / IT Director',    'Approves risk assessment results, authorizes risk acceptance decisions, reports to leadership.'],
        ]
      ),
      blank(),

      h1('8. Related Documents'),
      decimal('NIST SP 800-171 Rev 2 — Risk Assessment (3.11.1 - 3.11.3)'),
      decimal('NIST SP 800-30 — Guide for Conducting Risk Assessments'),
      decimal('System Security Plan (SSP)'),
      decimal('Plan of Action and Milestones (POA&M)'),

      h1('9. Review and Approval'),
      body('This policy must be reviewed annually or after a significant change to the threat landscape. All updates require approval from the CISO or IT Director.'),
    ],
  });
}
